import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import _ from 'lodash';
import superadminServices from 'app/services/superadmin-services';
import GatewayStatusChip from 'app/shared/devices/GatewayStatusChip';
import { ResponsiveDataTable, SaveButton, SecondaryActionButton, StandardPageHeader } from 'app/shared/ui';
import { describeError, formatDateTime } from '../vision/visionDisplay';

// El estado lo manda AWS cada 5 minutos (y al conectarse o desconectarse).
const REFRESH_INTERVAL_MS = 60 * 1000;

const Mono = ({ children }) => (
  <Typography component="span" variant="body2" sx={{ fontFamily: 'monospace' }}>{children}</Typography>
);

// Cambia el nombre y el local de un gateway. Elegir un local lo fija (deja de asociarse solo); «Asociar solo» lo suelta.
const EditGatewayDialog = ({ gateway, companies, onClose, onSaved }) => {
  const [name, setName] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [storeId, setStoreId] = useState('');
  const [stores, setStores] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!gateway) return;
    setName(gateway.name || '');
    setCompanyId(gateway.company?.id || '');
    setStoreId(gateway.store?.id || '');
    setError(null);
  }, [gateway]);

  useEffect(() => {
    if (!companyId) {
      setStores([]);
      return;
    }
    superadminServices.getDeviceEditOptions({ companyId })
      .then((resp) => setStores(resp?.stores || []))
      .catch(() => setStores([]));
  }, [companyId]);

  const save = async (patch) => {
    setSaving(true);
    setError(null);
    try {
      await superadminServices.updateGateway(gateway.id, patch);
      onSaved?.();
      onClose();
    } catch (requestError) {
      setError(describeError(requestError, 'No se pudo guardar el gateway'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!gateway} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Gateway {gateway?.display_name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField label="Nombre" value={name} onChange={(event) => setName(event.target.value)} fullWidth />
          <Autocomplete
            options={companies}
            getOptionLabel={(company) => company?.name || ''}
            value={_.find(companies, { id: companyId }) || null}
            onChange={(event, company) => {
              setCompanyId(company?.id || '');
              setStoreId('');
            }}
            renderInput={(params) => <TextField {...params} label="Empresa" />}
          />
          <Autocomplete
            options={stores}
            getOptionLabel={(store) => store?.name || ''}
            value={_.find(stores, { id: storeId }) || null}
            onChange={(event, store) => setStoreId(store?.id || '')}
            disabled={!companyId}
            renderInput={(params) => <TextField {...params} label="Local" />}
          />
          <Typography variant="caption" color="text.secondary">
            {gateway?.store_locked
              ? 'El local está fijado a mano. «Asociar solo» lo vuelve a tomar del local de los sensores que escucha.'
              : 'Se asocia solo al local de los sensores que escucha. Elegir un local lo fija.'}
          </Typography>
          {!!error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        {gateway?.store_locked && (
          <Button onClick={() => save({ name, store_id: '' })} disabled={saving}>Asociar solo</Button>
        )}
        <SaveButton onClick={() => save({ name, store_id: storeId || '' })} loading={saving} disabled={saving}>
          Guardar
        </SaveButton>
      </DialogActions>
    </Dialog>
  );
};

// Gateways LoRaWAN de AWS IoT Core: aparecen solos con el primer informe de estado y se asocian al local de los sensores
// que escuchan. Su conexión alimenta los avisos «Local sin conexión».
const GatewaysIndex = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [editing, setEditing] = useState(null);

  const loadRecords = useCallback((silent = false) => {
    if (!silent) setLoading(true);
    superadminServices.getGateways()
      .then((resp) => setRecords(resp?.data || []))
      .catch((error) => setNotice({ severity: 'error', text: describeError(error, 'No se pudo cargar los gateways') }))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadRecords();
    const timer = setInterval(() => loadRecords(true), REFRESH_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [loadRecords]);

  useEffect(() => {
    superadminServices.getCompanies('?page=1&include_archived=false')
      .then((resp) => setCompanies(resp?.data || []))
      .catch(() => setCompanies([]));
  }, []);

  const placeLabel = (record) => {
    const place = [record.company?.name, record.store?.name].filter(Boolean).join(' · ');
    if (!place) return 'Sin local';
    return place;
  };

  const columns = [
    { key: 'name', label: 'Gateway', render: (record) => record.display_name },
    { key: 'gateway_eui', label: 'EUI', render: (record) => <Mono>{record.gateway_eui || '-'}</Mono> },
    {
      key: 'place',
      label: 'Empresa · local',
      render: (record) => (
        <Stack direction="row" spacing={0.75} alignItems="center">
          <span>{placeLabel(record)}</span>
          {record.store_locked && <Chip size="small" variant="outlined" label="Fijado" />}
        </Stack>
      ),
    },
    { key: 'status', label: 'Conexión', render: (record) => <GatewayStatusChip gateway={record} /> },
    { key: 'heard_devices', label: 'Sensores', render: (record) => record.heard_devices },
    { key: 'last_report_at', label: 'Último informe', render: (record) => formatDateTime(record.last_report_at) },
    {
      key: 'actions',
      label: 'Acciones',
      align: 'right',
      mobileHidden: true,
      render: (record) => <Button size="small" onClick={() => setEditing(record)}>Editar</Button>,
    },
  ];

  return (
    <Box>
      <StandardPageHeader
        title="Superadministración · Gateways"
        subtitle="Gateways LoRaWAN de AWS IoT Core. Aparecen solos y se asocian al local de los sensores que escuchan."
        actions={<SecondaryActionButton startIcon={<RefreshOutlinedIcon />} onClick={() => loadRecords()}>Actualizar</SecondaryActionButton>}
      />

      {!!notice && (
        <Alert severity={notice.severity} sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice.text}
        </Alert>
      )}

      <Paper sx={{ p: 2, borderRadius: 2 }}>
        {loading ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <ResponsiveDataTable
            columns={columns}
            rows={records}
            rowKey="id"
            emptyMessage="Todavía no llega el estado de ningún gateway desde AWS."
            mobileTitle={(record) => record.display_name}
            mobileSubtitle={(record) => placeLabel(record)}
            renderRowActions={(record) => <Button size="small" onClick={() => setEditing(record)}>Editar</Button>}
          />
        )}
      </Paper>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Sensores: los que enviaron su última lectura por ese gateway en los últimos 7 días.
      </Typography>

      <EditGatewayDialog gateway={editing} companies={companies} onClose={() => setEditing(null)} onSaved={() => loadRecords(true)} />
    </Box>
  );
};

export default GatewaysIndex;
